import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import LoginPrompt from '../components/LoginPrompt';
import PremiumBadge from '../components/PremiumBadge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Download, 
  Trash2, 
  Settings, 
  HardDrive, 
  Wifi, 
  MapPin,
  Clock,
  FileText,
  Route,
  Camera,
  AlertCircle,
  CheckCircle,
  Play,
  Pause,
  RotateCcw,
  Crown,
  ArrowRight,
  Shield,
  Zap,
  Users
} from 'lucide-react';

const OfflineManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('browse');
  const [availableContent, setAvailableContent] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [cityPacks, setCityPacks] = useState([]);
  const [storageStats, setStorageStats] = useState({});
  const [userSettings, setUserSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if user has offline access permissions
  const hasOfflineAccess = user && (user.membership_tier === 'wanderloop_plus' || user.membership_tier === 'pro_supporter');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (hasOfflineAccess) {
      fetchOfflineData();
    }
  }, [hasOfflineAccess]);

  const fetchOfflineData = async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${user.token || 'mock-token'}`,
        'Content-Type': 'application/json'
      };

      // Fetch available content
      const contentResponse = await fetch(`${backendUrl}/api/offline/available-content`, { headers });
      if (contentResponse.ok) {
        const contentData = await contentResponse.json();
        setAvailableContent(contentData.content || []);
      }

      // Fetch user downloads
      const downloadsResponse = await fetch(`${backendUrl}/api/offline/downloads`, { headers });
      if (downloadsResponse.ok) {
        const downloadsData = await downloadsResponse.json();
        setDownloads(downloadsData || []);
      }

      // Fetch city packs
      const packsResponse = await fetch(`${backendUrl}/api/offline/city-packs`, { headers });
      if (packsResponse.ok) {
        const packsData = await packsResponse.json();
        setCityPacks(packsData || []);
      }

      // Fetch storage stats
      const statsResponse = await fetch(`${backendUrl}/api/offline/storage-stats`, { headers });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStorageStats(statsData || {});
      }

    } catch (err) {
      setError('Failed to load offline data');
      console.error('Error fetching offline data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (contentId, quality = 'medium') => {
    try {
      const headers = {
        'Authorization': `Bearer ${user.token || 'mock-token'}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${backendUrl}/api/offline/download`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content_id: contentId, quality })
      });

      if (response.ok) {
        const result = await response.json();
        // Refresh downloads list
        fetchOfflineData();
        alert('Download started successfully!');
      } else {
        const errorData = await response.json();
        alert(`Download failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      alert('Download failed: Network error');
    }
  };

  const handleDeleteDownload = async (downloadId) => {
    try {
      const headers = {
        'Authorization': `Bearer ${user.token || 'mock-token'}`,
      };

      const response = await fetch(`${backendUrl}/api/offline/downloads/${downloadId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        // Refresh downloads list
        fetchOfflineData();
        alert('Download deleted successfully!');
      } else {
        alert('Failed to delete download');
      }
    } catch (err) {
      alert('Failed to delete download: Network error');
    }
  };

  const syncOfflineActivities = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${user.token || 'mock-token'}`,
      };

      const response = await fetch(`${backendUrl}/api/offline/activities/sync`, { headers });
      if (response.ok) {
        const result = await response.json();
        alert(result.message || 'Sync completed successfully!');
      } else {
        alert('Sync failed');
      }
    } catch (err) {
      alert('Sync failed: Network error');
    }
  };

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'walk': return <MapPin className="w-5 h-5" />;
      case 'route': return <Route className="w-5 h-5" />;
      case 'journal': return <FileText className="w-5 h-5" />;
      case 'city_pack': return <Camera className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'downloading': return <Download className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'pending': return <Clock className="w-5 h-5 text-yellow-500" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 MB';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!user || !hasOfflineAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <SEOHead 
          title="Wanderloop Offline - Premium Feature"
          description="Download walks, routes, and journals for offline access"
        />
        <Header />
        
        {/* Hero Illustration Section */}
        <section className="relative py-20 overflow-hidden">
          {/* Background illustration elements */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200/30 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-40 h-40 bg-indigo-200/30 rounded-full blur-xl animate-pulse delay-300"></div>
            <div className="absolute top-40 right-1/4 w-24 h-24 bg-purple-200/30 rounded-full blur-lg animate-pulse delay-700"></div>
          </div>
          
          <div className="relative max-w-6xl mx-auto px-4 text-center">
            {/* Main illustration */}
            <div className="mb-12">
              <div className="relative inline-block">
                <div className="w-80 h-60 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl shadow-2xl mx-auto relative overflow-hidden">
                  {/* Illustrated landscape background */}
                  <div className="absolute inset-0 bg-gradient-to-b from-blue-200 to-indigo-300 opacity-70"></div>
                  
                  {/* Mountains */}
                  <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 320 120" className="w-full h-20 text-indigo-300">
                      <path d="M0,120 L50,60 L100,80 L150,40 L200,70 L250,30 L300,60 L320,50 L320,120 Z" fill="currentColor" />
                    </svg>
                  </div>
                  
                  {/* Phone/device downloading */}
                  <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
                    <div className="w-20 h-20 bg-indigo-500 rounded-2xl flex items-center justify-center shadow-lg relative">
                      <Download className="w-8 h-8 text-white animate-pulse" />
                      {/* Download progress indicator */}
                      <div className="absolute -bottom-2 left-2 right-2 h-1 bg-white/30 rounded-full overflow-hidden">
                        <div className="h-full bg-white rounded-full w-2/3 animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating elements representing offline content */}
                  <div className="absolute top-8 right-8 w-6 h-6 bg-blue-400 rounded-full shadow-lg animate-bounce">
                    <MapPin className="w-4 h-4 text-white m-1" />
                  </div>
                  <div className="absolute bottom-8 left-8 w-4 h-4 bg-indigo-400 rounded-full shadow-lg animate-bounce delay-300">
                    <FileText className="w-3 h-3 text-white m-0.5" />
                  </div>
                </div>
                
                {/* Wanderloop+ badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-3 shadow-xl">
                  <HardDrive className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Never Miss an Adventure <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
                with Offline Access
              </span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed">
              Wanderloop Offline is an exclusive feature for premium members—<br />
              your adventures continue even without internet connection.
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Features List */}
              <div>
                <h2 className="text-3xl font-black text-gray-900 mb-8">
                  With Offline Access, You Can:
                </h2>
                
                <div className="space-y-6">
                  {[
                    {
                      icon: <Download className="h-6 w-6" />,
                      title: "Download unlimited walks & routes",
                      description: "for exploration without internet",
                      color: "from-blue-500 to-cyan-500"
                    },
                    {
                      icon: <MapPin className="h-6 w-6" />,
                      title: "Access detailed offline maps",
                      description: "with GPS navigation that works anywhere",
                      color: "from-green-500 to-emerald-500"
                    },
                    {
                      icon: <FileText className="h-6 w-6" />,
                      title: "Save journals & media locally",
                      description: "and sync when you're back online",
                      color: "from-purple-500 to-pink-500"
                    },
                    {
                      icon: <Shield className="h-6 w-6" />,
                      title: "Never lose your progress",
                      description: "automatic sync keeps everything safe",
                      color: "from-orange-500 to-red-500"
                    }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-4 group">
                      <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-110`}>
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900 mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Demo Preview */}
              <div>
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-xl">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <HardDrive className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl text-gray-900">Offline Content Library</h3>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Premium Feature</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-6">
                    {/* Download Progress Examples */}
                    <div className="space-y-3">
                      {[
                        { name: "Griffith Observatory Trail", progress: 100, status: "completed" },
                        { name: "Venice Beach Boardwalk", progress: 67, status: "downloading" },
                        { name: "Hollywood Hills Route", progress: 0, status: "queued" }
                      ].map((item, index) => (
                        <div key={index} className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-blue-600" />
                              <span className="font-semibold text-gray-900 text-sm">{item.name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {item.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                              {item.status === 'downloading' && <Download className="h-4 w-4 text-blue-500 animate-pulse" />}
                              {item.status === 'queued' && <Clock className="h-4 w-4 text-gray-400" />}
                              <span className="text-xs text-gray-600 capitalize">{item.status}</span>
                            </div>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-300 ${
                                item.status === 'completed' ? 'bg-green-500' : 
                                item.status === 'downloading' ? 'bg-blue-500' : 'bg-gray-300'
                              }`}
                              style={{ width: `${item.progress}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Storage Info */}
                    <div className="bg-gradient-to-r from-green-100 to-blue-100 p-4 rounded-lg border-l-4 border-green-400">
                      <div className="flex items-center gap-2 mb-2">
                        <HardDrive className="h-5 w-5 text-green-600" />
                        <span className="font-bold text-green-800">2.3 GB available storage</span>
                      </div>
                      <p className="text-sm text-gray-700">Premium members get 5GB offline storage</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 text-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-10 w-20 h-20 bg-white/20 rounded-full animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/10 rounded-full animate-pulse delay-300"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/15 rounded-full animate-pulse delay-700"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-black mb-8">
              Ready for Offline Adventures?
            </h2>
            
            <p className="text-xl mb-12 opacity-90 max-w-2xl mx-auto">
              Download your favorite walks, routes, and journals. Explore LA without limits, even without signal.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Button 
                size="lg"
                onClick={() => navigate('/membership')}
                className="bg-white text-blue-600 hover:bg-gray-100 font-black px-12 py-4 text-xl shadow-2xl hover:shadow-white/25 transform hover:scale-105 transition-all duration-300"
              >
                <Crown className="mr-3 h-6 w-6" />
                Upgrade to Premium
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </div>
            
            <div className="flex items-center justify-center gap-8 text-sm opacity-80">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>7-day free trial</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>12,500+ members</span>
              </div>
            </div>
          </div>
        </section>

        <FloatingActions />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEOHead 
        title="Wanderloop Offline - Download Manager"
        description="Manage your offline content downloads and storage"
      />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Wanderloop Offline
            <PremiumBadge className="ml-2" />
          </h1>
          <p className="text-gray-600">
            Download and manage your offline content for adventures without internet
          </p>
        </div>

        {/* Storage Overview */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <HardDrive className="w-5 h-5 mr-2" />
            Storage Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {storageStats.total_downloads || 0}
              </div>
              <div className="text-sm text-gray-600">Total Downloads</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {storageStats.total_storage_mb || 0} MB
              </div>
              <div className="text-sm text-gray-600">Storage Used</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {storageStats.storage_percentage || 0}%
              </div>
              <div className="text-sm text-gray-600">Storage Full</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {storageStats.downloads_remaining === -1 ? '∞' : storageStats.downloads_remaining || 0}
              </div>
              <div className="text-sm text-gray-600">Downloads Left</div>
            </div>
          </div>
          
          {/* Storage Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Storage Usage</span>
              <span>{storageStats.storage_limit_mb || 1000} MB limit</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(storageStats.storage_percentage || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: 'browse', label: 'Browse Content', icon: MapPin },
                { id: 'downloads', label: 'My Downloads', icon: Download },
                { id: 'packs', label: 'City Packs', icon: Camera },
                { id: 'sync', label: 'Sync & Activities', icon: RotateCcw }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading offline data...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600">{error}</p>
                <button
                  onClick={fetchOfflineData}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : (
              <>
                {/* Browse Content Tab */}
                {activeTab === 'browse' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Available for Download
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {availableContent.map(content => (
                        <div key={content.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                                {getContentIcon(content.content_type)}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{content.title}</h4>
                                <p className="text-sm text-gray-600 mt-1">{content.description}</p>
                                <div className="flex items-center mt-2 text-sm text-gray-500">
                                  <span className="capitalize">{content.content_type}</span>
                                  <span className="mx-2">•</span>
                                  <span>{formatFileSize(content.file_size_bytes)}</span>
                                  <span className="mx-2">•</span>
                                  <span>{content.download_count} downloads</span>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDownload(content.id)}
                              disabled={content.is_downloaded}
                              className={`flex items-center px-3 py-1 rounded-lg text-sm font-medium ${
                                content.is_downloaded
                                  ? 'bg-green-100 text-green-700 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {content.is_downloaded ? (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Downloaded
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-1" />
                                  Download
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Downloads Tab */}
                {activeTab === 'downloads' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      My Downloads
                    </h3>
                    <div className="space-y-4">
                      {downloads.map(download => (
                        <div key={download.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                {getStatusIcon(download.status)}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">
                                  {download.content?.title || 'Unknown Content'}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {download.content?.description || 'No description'}
                                </p>
                                <div className="flex items-center mt-2 text-sm text-gray-500">
                                  <span className="capitalize">{download.content?.content_type || 'Unknown'}</span>
                                  <span className="mx-2">•</span>
                                  <span>{formatFileSize(download.file_size_bytes)}</span>
                                  <span className="mx-2">•</span>
                                  <span className="capitalize">{download.status}</span>
                                  {download.progress_percentage > 0 && download.progress_percentage < 100 && (
                                    <>
                                      <span className="mx-2">•</span>
                                      <span>{download.progress_percentage}%</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteDownload(download.id)}
                              className="flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* City Packs Tab */}
                {activeTab === 'packs' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      City Packs
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {cityPacks.map(pack => (
                        <div key={pack.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="aspect-w-16 aspect-h-9 mb-4">
                            <img
                              src={pack.thumbnail_url || '/api/placeholder/300/200'}
                              alt={pack.title}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                          </div>
                          <h4 className="font-semibold text-gray-900">{pack.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{pack.description}</p>
                          <div className="flex items-center mt-2 text-sm text-gray-500">
                            <span>{pack.city_name}, {pack.country}</span>
                          </div>
                          <div className="flex items-center justify-between mt-4">
                            <div className="text-sm text-gray-500">
                              {pack.total_walks} walks • {pack.total_distance_km} km
                            </div>
                            <button
                              onClick={() => handleDownload(pack.id)}
                              className="flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sync & Activities Tab */}
                {activeTab === 'sync' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Sync & Activities
                    </h3>
                    <div className="space-y-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Sync Offline Activities</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Sync your offline activities like completed walks, photos taken, and route progress with the cloud.
                        </p>
                        <button
                          onClick={syncOfflineActivities}
                          className="flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Sync Now
                        </button>
                      </div>
                      
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Offline Mode Status</h4>
                        <div className="flex items-center text-sm text-gray-600">
                          <Wifi className="w-4 h-4 mr-2" />
                          <span>Connected to internet</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          All your offline content is available and activities will sync automatically.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfflineManager;